<template>
  <div class="whatsapp-message-preview">
    <div
      class="bg-gray-100 rounded-lg p-4 min-h-[400px] max-w-[300px] mx-auto"
      style="background-color: #e5ddd5;"
    >
      <div class="flex flex-col max-h-96 overflow-auto">
        <div class="flex-grow">
          <!-- Message Preview -->
          <div
            v-if="processedMessage"
            class="bg-white rounded-lg p-3 text-sm inline-block max-w-[85%] shadow-sm w-full"
            style="border-radius: 8px 8px 8px 0"
          >
            <!-- Image Preview -->
            <div
              v-if="template.messageType === 'image' && template.mediaUrl"
              class="mb-3"
            >
              <img
                :src="template.mediaUrl"
                alt="Message image"
                class="w-full h-32 object-cover rounded-lg"
                @error="handleImageError"
                style="max-height: 120px;"
              />
            </div>

            <!-- Message Text -->
            <div style="white-space: pre-line;">
              {{ processedMessage }}
            </div>

            <!-- Button Preview -->
            <div
              v-if="
                shouldShowButton || (template.buttonText && template.buttonUrl)
              "
              class="mt-3 text-center border-t-2"
            >
              <a
                class="inline-block text-blue-500 px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#075E54] transition-colors w-full text-center"
              >
                {{ buttonText }}
              </a>
            </div>

            <!-- Timestamp -->
            <div class="text-[11px] text-gray-500 text-right mt-1">
              {{ currentTime }}
            </div>
          </div>

          <!-- Empty State -->
          <div
            v-else
            class="bg-white rounded-lg p-3 text-sm inline-block max-w-[85%] text-gray-400 italic shadow-sm"
            style="border-radius: 8px 8px 8px 0"
          >
            No message content available...
            <div class="text-[11px] text-gray-500 text-right mt-1">
              {{ currentTime }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WhatsappMessagePreview",
  props: {
    template: {
      type: Object,
      required: true,
      default: () => ({
        message: "",
        mediaUrl: "",
        buttonText: "",
        buttonUrl: "",
        variables: []
      })
    },

    // Optional sample data to replace variables
    sampleData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      currentTime: ""
    };
  },
  computed: {
    processedMessage() {
      if (!this.template.message) {
        return "";
      }

      let message = this.template.message;

      // Replace variables with sample data or placeholder values
      if (this.template.variables && this.template.variables.length > 0) {
        this.template.variables.forEach(variable => {
          const placeholder = this.getPlaceholderValue(
            variable.replacingElement
          );
          message = message.replace(
            new RegExp(variable.variable, "g"),
            placeholder
          );
        });
      }

      message = message.replace(/\[(.*?),https?:\/\/[^\]]+\]/g, "");
      // Remove [STOP] from message
      message = message.replace(/\[STOP\]/g, "");
      return message;
    },
    shouldShowButton() {
      if (!this.template.variables || this.template.variables.length === 0) {
        return false;
      }

      // Check for [text,url] pattern with variable
      const buttonPattern = /\[(.*?),https:\/\/.*?{{.*?}}\]/;
      if (this.template.message && buttonPattern.test(this.template.message)) {
        return true;
      }

      const lastVariable = this.template.variables[
        this.template.variables.length - 1
      ];
      const slugTypes = [
        "checkout_slug",
        "order_status_slug",
        "review_request_slug",
        "cod_confirmation_page_slug"
      ];

      return slugTypes.some(slugType =>
        lastVariable.replacingElement.includes(slugType)
      );
    },
    buttonText() {
      if (!this.shouldShowButton) {
        return this.template.buttonText || "";
      }

      const lastVariable = this.template.variables[
        this.template.variables.length - 1
      ];

      // Check for [text,url] pattern with variable in message
      const buttonMatch = this.template.message?.match(
        /\[(.*?),https:\/\/.*?{{.*?}}\]/
      );
      if (buttonMatch && buttonMatch[1]) {
        return buttonMatch[1];
      }

      // Determine button text based on slug type
      if (lastVariable.replacingElement.includes("checkout_slug")) {
        return "{{checkout_button}}";
      } else if (lastVariable.replacingElement.includes("order_status_slug")) {
        return "{{orderStatusButton}}";
      } else if (
        lastVariable.replacingElement.includes("review_request_slug")
      ) {
        return "{{reviewButton}}";
      } else if (
        lastVariable.replacingElement.includes("cod_confirmation_page_slug")
      ) {
        return "{{confirmOrderButton}}";
      }

      return this.template.buttonText || "Click Here";
    },
    buttonUrl() {
      if (!this.shouldShowButton) {
        return this.template.buttonUrl || "";
      }

      const lastVariable = this.template.variables[
        this.template.variables.length - 1
      ];
      const placeholder = this.getPlaceholderValue(
        lastVariable.replacingElement
      );

      // If it's a slug, construct the URL
      if (placeholder && placeholder !== lastVariable.replacingElement) {
        return placeholder;
      }

      return this.template.buttonUrl;
    }
  },
  mounted() {
    this.updateTime();
    // Update time every minute
    this.timeInterval = setInterval(this.updateTime, 60000);
  },
  beforeDestroy() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  },
  methods: {
    updateTime() {
      const now = new Date();
      this.currentTime = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      });
    },
    handleImageError(event) {
      event.target.src = "https://via.placeholder.com/150?text=Image+Error";
    },
    getPlaceholderValue(replacingElement) {
      // If sample data is provided, use it
      if (this.sampleData && this.sampleData[replacingElement]) {
        return this.sampleData[replacingElement];
      }

      // Otherwise, provide meaningful placeholder values
      const placeholderMap = {
        "{{first_name}}": "John",
        "{{last_name}}": "Doe",
        "{{full_name}}": "John Doe",
        "{{shop_name}}": "My Store",
        "{{phoneNumber}}": "+1 (555) 123-4567",
        "{{email}}": "customer@example.com",
        "{{order_number}}": "#12345",
        "{{order_total}}": "$99.99",
        "{{product_name}}": "Sample Product",
        "{{review_request_slug}}": "https://example.com/review/abc123",
        "{{checkout_slug}}": "https://example.com/checkout/xyz789",
        "{{order_status_slug}}": "https://example.com/track/def456",
        "{{cod_confirmation_page_slug}}": "https://example.com/confirm/ghi789",
        "{{coupon_code}}": "SAVE10",
        "{{discount_amount}}": "10%",
        "{{customer_id}}": "CUST001",
        "{{order_date}}": "2024-01-15",
        "{{delivery_date}}": "2024-01-20",
        "{{tracking_number}}": "TRK123456789",
        "{{shipping_address}}": "123 Main St, City, State 12345"
      };

      // Remove the curly braces for lookup
      const key = replacingElement.replace(/[{}]/g, "");
      return placeholderMap[key] || replacingElement;
    }
  }
};
</script>

<style scoped>
.whatsapp-message-preview {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
}

.whatsapp-message-preview img {
  border-radius: 8px;
  object-fit: cover;
}

.whatsapp-message-preview a {
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.whatsapp-message-preview a:hover {
  background-color: #f0f0f0;
}
</style>
